import {
  ChangeDetectionStrategy,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import '@neo-ds/components/alert';
import { ScreenSizeService } from '../../../core/services/screen-size.service';
import {
  CcfNotificationLayout,
  CcfNotificationRole,
  CcfNotificationState,
  NeoAlertToggleEvent,
} from 'src/app/core/interfaces/notification.interface';

@Component({
  selector: 'app-ccf-notification',
  imports: [],
  templateUrl: './ccf-notification.component.html',
  styleUrl: './ccf-notification.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'ccf-notification d-block',
    '[class.ccf-notification--inline]': "layout() === 'inline'",
    '[class.ccf-notification--popup]': "layout() === 'popup'",
    '[class.mobile]': 'isMobile()',
    '[style.--sizing-notification-padding-horizontal-full]': "'var(--sizing-notification-padding-horizontal-grid)'",
    '[style.width]': 'hostWidth()',
    '[style.max-width]': 'hostMaxWidth()',
  },
})
export class CcfNotificationComponent {
  private readonly screenSizeService = inject(ScreenSizeService);

  private readonly isVisible = signal(true);

  message = input.required<string>();
  state = input<CcfNotificationState>('info');
  brand = input<string>('BMW');
  variant = input<string>('full-width');
  expanded = input<boolean>(true);
  dismissible = input<boolean>(false);
  role = input<CcfNotificationRole>('status');
  layout = input<CcfNotificationLayout>('inline');
  expiryTime = input<number | null>(null);

  readonly dismissed = output<void>();

  protected readonly visible = this.isVisible.asReadonly();
  protected readonly isMobile = this.screenSizeService.isPhoneOrPortraitTablet;
  protected readonly ariaLive = computed(() => (this.role() === 'status' ? 'polite' : 'assertive'));
  protected readonly alertType = computed(() => (this.state() === 'info' ? 'informative' : this.state()));
  protected readonly hostWidth = computed(() => (this.layout() === 'popup' ? '30rem' : null));
  protected readonly hostMaxWidth = computed(() =>
    this.layout() === 'popup' && this.isMobile() ? 'calc(100vw - 2rem)' : null
  );

  constructor() {
    effect((onCleanup) => {
      this.message();

      const expiryTime = this.expiryTime();
      this.isVisible.set(true);

      if (expiryTime === null || expiryTime <= 0) {
        return;
      }

      const timer = setTimeout(() => this.isVisible.set(false), expiryTime);
      onCleanup(() => clearTimeout(timer));
    });
  }

  protected onToggle(event: Event): void {
    const detail = (event as NeoAlertToggleEvent).detail;
    if (detail?.open === false) {
      this.isVisible.set(false);
      this.dismissed.emit();
    }
  }
}
